/**
 * React konteksts lietotāja tiesībām.
 *
 * Lietošana:
 *   <PermissionsProvider>
 *     <App />
 *   </PermissionsProvider>
 *
 *   const { has, role, loading } = usePermissions();
 *   if (!has('invoice.create')) return null;
 *
 * Katalogs (visu iespējamo tiesību saraksts) tiek ielādēts no
 * GET /api/permissions/catalog. Pašreizējā lietotāja tiesības — no
 * GET /api/permissions/me.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from './api';

const PermissionsContext = createContext({
  loading: true,
  role: null,
  perms: new Set(),
  has: () => false,
  reload: () => {},
});

export function PermissionsProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [role,    setRole]    = useState(null);
  const [perms,   setPerms]   = useState(new Set());

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/permissions/me');
      setRole(data?.role || null);
      setPerms(new Set(Array.isArray(data?.perms) ? data.perms : []));
    } catch (e) {
      // Ja serveris vēl nav palaidis migrācijas / neatrod /permissions,
      // neblokjam UI — lietotājs redzēs 403 uz konkrētām darbībām.
      setRole(null);
      setPerms(new Set());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const has = useCallback((key) => {
    if (role === 'admin') return true;
    return perms.has(key);
  }, [role, perms]);

  return (
    <PermissionsContext.Provider value={{ loading, role, perms, has, reload: load }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionsContext);
}

// Nosaukumi, kas jāsinhronizē ar /server/src/permissions/index.js PERMS
export const P = {
  INVOICE_VIEW_ALL:   'invoice.view.all',
  INVOICE_VIEW_OWN:   'invoice.view.own',
  INVOICE_CREATE:     'invoice.create',
  INVOICE_EDIT:       'invoice.edit',
  INVOICE_DELETE:     'invoice.delete',
  INVOICE_SEND:       'invoice.send',
  INVOICE_EXPORT:     'invoice.export',

  CLIENT_VIEW:        'client.view',
  CLIENT_CREATE:      'client.create',
  CLIENT_EDIT:        'client.edit',
  CLIENT_DELETE:      'client.delete',

  PRODUCT_VIEW:       'product.view',
  PRODUCT_CREATE:     'product.create',
  PRODUCT_EDIT:       'product.edit',
  PRODUCT_DELETE:     'product.delete',

  EXPENSE_VIEW_ALL:   'expense.view.all',
  EXPENSE_VIEW_OWN:   'expense.view.own',
  EXPENSE_CREATE:     'expense.create',
  EXPENSE_EDIT:       'expense.edit',
  EXPENSE_DELETE:     'expense.delete',

  EMPLOYEE_VIEW:      'employee.view',
  EMPLOYEE_MANAGE:    'employee.manage',
  PAYROLL_VIEW:       'payroll.view',
  PAYROLL_MANAGE:     'payroll.manage',

  SUPPLIER_VIEW:      'supplier.view',
  SUPPLIER_MANAGE:    'supplier.manage',

  REPORTS_VIEW:       'reports.view',
  BANK_VIEW:          'bank.view',
  BANK_MANAGE:        'bank.manage',
  ASSETS_VIEW:        'assets.view',
  ASSETS_MANAGE:      'assets.manage',

  COMPANY_SETTINGS:   'company.settings',
  USER_MANAGE:        'user.manage',
  PERMISSIONS_MANAGE: 'user.permissions.manage',

  INVENTORY_VIEW:     'inventory.view',
  INVENTORY_CREATE:   'inventory.create',
  INVENTORY_EDIT:     'inventory.edit',
  INVENTORY_DELETE:   'inventory.delete',
  INVENTORY_ISSUE:    'inventory.issue',
  INVENTORY_RETURN:   'inventory.return',
  INVENTORY_MOVE:     'inventory.move',

  PROJECT_VIEW:       'project.view',
  PROJECT_CREATE:     'project.create',
  PROJECT_EDIT:       'project.edit',
  PROJECT_CLOSE:      'project.close',

  WAREHOUSE_MANAGE:   'warehouse.manage',
  QR_GENERATE:        'qr.generate',
  LABEL_PRINT:        'label.print',
  STOCKTAKE_MANAGE:   'stocktake.manage',
  SERVICE_MANAGE:     'service.manage',
};

/**
 * Apvienota "view" tiesība: ja lietotājam ir .view.all VAI .view.own — drīkst skatīt.
 */
export function canView(has, allKey, ownKey) {
  return has(allKey) || has(ownKey);
}
